// typescript
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import {
  DomSanitizer,
  SafeHtml
} from "@angular/platform-browser";

@Component ( {
  selector: 'reporter-business-unit-details',
  templateUrl: './business-unit-details.component.html',
  styleUrls: [ './business-unit-details.component.scss' ],
  encapsulation: ViewEncapsulation.None,
  standalone: false
} )
export class BusinessUnitDetailsComponent implements OnChanges {

  @Input () showClientNumber = false;
  @Input () buDetails: any;
  @Input () buReferenceNumberPrompt: string;
  @Input () searchTerm = '';
  @Input () searchCriteria: any = null;

  buDetailsLine = '';
  buDetailsLineSafe: SafeHtml | null = null;


  constructor (
    private sanitizer: DomSanitizer
  ) {
  }

  ngOnChanges ( changes: SimpleChanges ) {
    if ( changes.buDetails?.currentValue ) {
      const d = this.buDetails || {};
      const parts: string[] = [];

      const labelClient = `<b>Client Number:</b>`;
      const labelReference = this.buReferenceNumberPrompt ? `<b>${ this.buReferenceNumberPrompt }:</b>` : '';
      const labelAddress = `<b>Address:</b>`;
      const labelPhone = `<b>Phone:</b>`;

      if ( this.showClientNumber && d.gbClientNumber != null && d.gbClientNumber !== '' ) {
        parts.push ( `${ labelClient } ${ d.gbClientNumber }` );
      }

      if ( this.buReferenceNumberPrompt && d.referenceNumber != null && d.referenceNumber !== '' ) {
        parts.push ( `${ labelReference } ${ d.referenceNumber }` );
      }

      // Build address only from present components
      const addressSegments: string[] = [];
      if ( d.address1 ) {
        addressSegments.push ( d.address1 );
      }
      if ( d.address2 ) {
        addressSegments.push ( d.address2 );
      }

      // Build locality as "City, JURISDICTION PostalCode" where appropriate
      const city = d.city ? String ( d.city ).trim () : '';
      const jurisdictionPrefix = d.jurisdiction ? String ( d.jurisdiction ).split ( '_' )[ 0 ] : '';
      const postal = d.postalCode ? String ( d.postalCode ).trim () : '';

      let localityStr = '';
      if ( city && jurisdictionPrefix ) {
        localityStr = `${ city }, ${ jurisdictionPrefix }`;
      } else if ( city ) {
        localityStr = city;
      } else if ( jurisdictionPrefix ) {
        localityStr = jurisdictionPrefix;
      }

      if ( postal ) {
        localityStr = localityStr ? `${ localityStr } ${ postal }` : postal;
      }

      const addressParts: string[] = [];
      if ( addressSegments.length ) {
        addressParts.push ( addressSegments.join ( ', ' ) );
      }
      if ( localityStr ) {
        addressParts.push ( localityStr );
      }

      if ( addressParts.length ) {
        parts.push ( `${ labelAddress } ${ addressParts.join ( ', ' ) }` );
      }

      if ( d.phone ) {
        parts.push ( `${ labelPhone } ${ d.phone }` );
      }

      // join using semicolon and space similar to previous formatting
      this.buDetailsLine = parts.join ( '; ' );

      // Build highlight terms from single searchTerm and the searchCriteria fields.
      const termsToHighlight: string[] = [];

      if ( this.searchTerm && String ( this.searchTerm ).trim () ) {
        termsToHighlight.push ( String ( this.searchTerm ).trim () );
      }

      if ( this.searchCriteria && typeof this.searchCriteria === 'object' ) {
        const sc = this.searchCriteria;
        const pushIf = ( v: any ) => {
          if ( v != null ) {
            const s = String ( v ).trim ();
            if ( s ) {
              termsToHighlight.push ( s );
            }
          }
        };

        // Only include fields that may appear in the details line
        pushIf ( sc[ 'referenceNumber' ] );
        pushIf ( sc[ 'address' ] );
        pushIf ( sc[ 'city' ] );
        // jurisdiction may be a key like "NY_US" - highlight prefix if present
        if ( sc[ 'jurisdiction' ] ) {
          const j = String ( sc[ 'jurisdiction' ] );
          const jPrefix = j.split ( '_' )[ 0 ];
          if ( jPrefix ) {
            termsToHighlight.push ( jPrefix );
          }
        }
        pushIf ( sc[ 'postalCode' ] );
        pushIf ( sc[ 'phone' ] );
      }

      if ( termsToHighlight.length ) {
        // Protect specific labels from being highlighted by replacing them with placeholders.
        const labelsToProtect = [ labelClient, labelReference, labelPhone ].filter ( l => l );
        const placeholders: { token: string; label: string }[] = [];
        let protectedHtml = this.buDetailsLine;

        labelsToProtect.forEach ( ( label, idx ) => {
          const token = `__LABEL_TOKEN_${ idx }__`;
          if ( protectedHtml.includes ( label ) ) {
            protectedHtml = protectedHtml.split ( label ).join ( token );
            placeholders.push ( {
              token,
              label
            } );
          }
        } );

        // Build a single regex for all terms (escape special chars, sort by length desc to prefer longer matches)
        const uniqueTerms = Array.from ( new Set ( termsToHighlight.map ( t => t ) ) ).sort ( ( a, b ) => b.length - a.length );
        const escapedTerms = uniqueTerms.map ( t => this.escapeRegExp ( t ) ).filter ( t => t.length > 0 );
        if ( escapedTerms.length ) {
          const regex = new RegExp ( `(${ escapedTerms.join('|') })`, 'gi' );
          protectedHtml = protectedHtml.replace ( regex, '<span class="highlight">$1</span>' );
        }

        // Restore the protected labels back into the HTML
        placeholders.forEach ( p => {
          protectedHtml = protectedHtml.split ( p.token ).join ( p.label );
        } );

        this.buDetailsLine = protectedHtml;
      }

      this.buDetailsLineSafe = this.sanitizer.bypassSecurityTrustHtml ( this.buDetailsLine );
    }
  }

  private escapeRegExp ( value: string ): string {
    return value.replace ( /[.*+?^${}()|[\]\\]/g, '\\$&' );
  }
}
